import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatDubai } from '../../../utils/dubaiTime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faFileInvoice, faHourglassHalf, faFilePdf,
    faImage, faTrash, faUpload, faCheck,
} from '@fortawesome/free-solid-svg-icons';
import PaymentMethodsSection from '../ClientWallet/PaymentMethodsSection';
import './clientProfile.css';

const DOC_META = {
    trade_license: 'Trade License',
    vat_certificate: 'VAT Certificate',
    document: 'Document',
};

// Which fields a client can edit (everything else on the page is read-only).
const EDITABLE = ['client_type', 'company_name', 'trn_number', 'is_vat_registered',
    'address_line_1', 'city', 'state', 'country'];

const FIELD_LABELS = {
    client_type: 'account type',
    company_name: 'Company name',
    trn_number: 'TRN number',
    is_vat_registered: 'VAT registered',
    address_line_1: 'Address',
    city: 'City',
    state: 'State / Emirate',
    country: 'Country',
};

export default function ClientProfile() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(false);
    // Multi-billing (PLAN_multi_billing.md): non-empty only when the account
    // has multi-billing enabled — the card below renders off this list, so
    // regular clients see zero change.
    const [billingEntities, setBillingEntities] = useState([]);

    const fetchProfile = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/client-requests/requests/profile');
            setData(res.data);
            setError('');
        } catch (err) {
            console.error('Error loading profile:', err);
            setError('Could not load your account. Please refresh.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBillingEntities = useCallback(async () => {
        try {
            const res = await axios.get('/api/client-requests/requests/billing-entities');
            setBillingEntities(Array.isArray(res.data?.entities) ? res.data.entities : []);
        } catch (err) {
            // Silently degrade — the card simply doesn't render.
            setBillingEntities([]);
        }
    }, []);

    useEffect(() => { fetchProfile(); fetchBillingEntities(); }, [fetchProfile, fetchBillingEntities]);

    if (loading) {
        return (
            <div className="app-loading">
                <div className="app-spinner" />
                <div className="app-spinner-text">Loading your account…</div>
            </div>
        );
    }
    if (error || !data) {
        return <div className="cprof"><div className="cprof__error">{error || 'No account data.'}</div></div>;
    }

    const { client, user, billing_address: addr, property_count, documents, pending_request } = data;
    // Account "member since" = when the client account was created.
    // Asia/Dubai renders (item 8) — zoneless fromISO used the device TZ.
    const memberSince = client.registration
        ? formatDubai(client.registration, 'LLLL yyyy') : '—';
    // User "member since" = when this login was created (may differ).
    const userSince = user?.created_at
        ? formatDubai(user.created_at, 'LLLL yyyy') : '—';
    const isBusiness = client.client_type === 'business';
    // The logged-in person (users table) — may differ from the account.
    const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || '—' : '—';
    // The account/billing entity name.
    const accountName = client.display_name || client.company_name
        || `${client.first_name || ''} ${client.last_name || ''}`.trim() || '—';

    return (
        <div className="cprof">
            <div className="cprof__header">
                <h1 className="cprof__title">My Account</h1>
                {!pending_request && (
                    <button className="cprof__editBtn" onClick={() => setEditing(true)}>Edit Details</button>
                )}
            </div>

            {pending_request && (
                <div className="cprof__pending">
                    <span className="cprof__pendingChip"><FontAwesomeIcon icon={faHourglassHalf} /> Change pending review</span>
                    <span className="cprof__pendingSub">
                        Your requested changes are awaiting our team&apos;s approval — your details stay as-is until then.
                    </span>
                </div>
            )}

            {/* Snapshot stats */}
            <div className="cprof__stats">
                <div className="cprof__stat">
                    <FontAwesomeIcon icon={faBuilding} className="cprof__statIcon" />
                    <div>
                        <div className="cprof__statLabel">Account</div>
                        <div className="cprof__statValue">{accountName}</div>
                    </div>
                </div>
                <div className="cprof__stat">
                    <FontAwesomeIcon icon={faBuilding} className="cprof__statIcon" />
                    <div>
                        <div className="cprof__statLabel">Properties</div>
                        <div className="cprof__statValue">{property_count}</div>
                    </div>
                </div>
                <div className="cprof__stat">
                    <FontAwesomeIcon icon={faFileInvoice} className="cprof__statIcon" />
                    <div>
                        <div className="cprof__statLabel">Member since</div>
                        <div className="cprof__statValue">{memberSince}</div>
                    </div>
                </div>
            </div>

            {/* Logged-in user (person) — may differ from the account */}
            <div className="cprof__card">
                <div className="cprof__cardTitle">Signed in as</div>
                <div className="cprof__grid">
                    <Field label="Name" value={userName} />
                    <Field label="Email" value={user?.email} />
                    <Field label="Phone" value={user?.phone_number} />
                    <Field label="Member since" value={userSince} />
                </div>
            </div>

            {/* Account (billing entity) */}
            <div className="cprof__card">
                <div className="cprof__cardTitle">Account details</div>
                <div className="cprof__grid">
                    <Field label="Account name" value={accountName} />
                    <Field label="Account type" value={isBusiness ? 'Business account' : 'Individual'} />
                    <Field label="Billing email" value={client.email} />
                    {isBusiness && <Field label="Company name" value={client.company_name} />}
                    {isBusiness && <Field label="VAT registered" value={client.is_vat_registered ? 'Yes' : 'No'} />}
                    {isBusiness && client.is_vat_registered && <Field label="TRN number" value={client.trn_number} />}
                </div>
            </div>

            {/* Billing address (editable via request) */}
            <div className="cprof__card">
                <div className="cprof__cardTitle">Billing address</div>
                <div className="cprof__grid">
                    <Field label="Address" value={addr.address_line_1} wide />
                    <Field label="City" value={addr.city} />
                    <Field label="State / Emirate" value={addr.state} />
                    <Field label="Country" value={addr.country} />
                </div>
            </div>

            {/* Billing entities (multi-billing clients only) */}
            {billingEntities.length > 0 && (
                <BillingEntitiesCard entities={billingEntities} />
            )}

            {/* Documents */}
            <div className="cprof__card">
                <div className="cprof__cardTitle">Documents</div>
                {documents.length === 0 ? (
                    <div className="cprof__docsEmpty">
                        No documents yet. Add your trade license or VAT certificate via “Edit Details”.
                    </div>
                ) : (
                    <div className="cprof__docs">
                        {documents.map((d) => (
                            <a key={d.id} className="cprof__doc" href={d.file_url} target="_blank" rel="noopener noreferrer">
                                <FontAwesomeIcon icon={/\.pdf($|\?)/i.test(d.file_url) ? faFilePdf : faImage} className="cprof__docIcon" />
                                <div className="cprof__docMeta">
                                    <div className="cprof__docType">{DOC_META[d.type] || 'Document'}</div>
                                    <div className="cprof__docDate">
                                        {d.uploaded_at ? formatDubai(d.uploaded_at, 'dd LLL yyyy') : ''}
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                )}
            </div>

            {/* Saved cards (option 3 management) — add / remove, LOUD consent. */}
            <PaymentMethodsSection />

            {editing && (
                <EditProfileModal
                    client={client}
                    addr={addr}
                    documents={documents}
                    onClose={() => setEditing(false)}
                    onSubmitted={() => { setEditing(false); fetchProfile(); }}
                />
            )}
        </div>
    );
}

function Field({ label, value, wide }) {
    return (
        <div className={`cprof__field ${wide ? 'cprof__field--wide' : ''}`}>
            <div className="cprof__fieldLabel">{label}</div>
            <div className="cprof__fieldValue">{value || '—'}</div>
        </div>
    );
}

function EditProfileModal({ client, addr, documents = [], onClose, onSubmitted }) {
    const [form, setForm] = useState({
        client_type: client.client_type === 'business' ? 'business' : 'individual',
        company_name: client.company_name || '',
        trn_number: client.trn_number || '',
        is_vat_registered: !!client.is_vat_registered,
        address_line_1: addr.address_line_1 || '',
        city: addr.city || '',
        state: addr.state || '',
        country: addr.country || '',
    });
    const isBusiness = form.client_type === 'business';
    const original = React.useRef({ ...form }).current;
    // One dedicated file slot per document type.
    const [files, setFiles] = useState({ trade_license: null, vat_certificate: null });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const set = (patch) => setForm((p) => ({ ...p, ...patch }));
    const norm = (v) => (v == null ? '' : String(v).trim());
    const changedKeys = EDITABLE.filter((k) => (k === 'is_vat_registered'
        ? !!form[k] !== !!original[k] : norm(form[k]) !== norm(original[k])));
    // Which upload slots are visible: trade license for any business; the VAT
    // certificate slot only when the business is VAT registered.
    const visibleDocTypes = isBusiness
        ? ['trade_license', ...(form.is_vat_registered ? ['vat_certificate'] : [])] : [];
    // Only files staged in visible slots count (and get uploaded).
    const pickedTypes = visibleDocTypes.filter((t) => files[t]);

    // Documents already stored on the account satisfy the requirements — no
    // re-upload needed on every edit.
    const onFile = (type) => documents.some((d) => d.type === type);
    const docSatisfied = (type) => !!files[type] || onFile(type);

    // Required fields: full billing address for any account; company name for a
    // business; TRN when the business is VAT registered.
    const requiredFields = ['address_line_1', 'city', 'state', 'country'];
    if (isBusiness) requiredFields.push('company_name');
    if (isBusiness && form.is_vat_registered) requiredFields.push('trn_number');
    const missing = requiredFields.filter((k) => !norm(form[k]));

    // Required documents: a business must have a trade license on file (existing
    // or staged); a VAT-registered business additionally needs a VAT certificate.
    const missingDocs = visibleDocTypes.filter((t) => !docSatisfied(t));

    const hasChanges = changedKeys.length > 0 || pickedTypes.length > 0;
    const canSubmit = hasChanges && missing.length === 0 && missingDocs.length === 0;

    const setSlot = (type, file) => setFiles((p) => ({ ...p, [type]: file }));

    const uploadDocs = async () => {
        const out = [];
        for (const type of pickedTypes) {
            const file = files[type];
            const res = await axios.post('/api/client-requests/requests/profile/upload-document-url', {
                fileName: file.name, fileType: file.type,
            });
            const { url, fileKey, fileUrl } = res.data;
            await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type, 'x-amz-acl': 'public-read' }, body: file });
            out.push({ file_key: fileKey, file_url: fileUrl, type });
        }
        return out;
    };

    const submit = async () => {
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const body = {};
            for (const k of changedKeys) body[k] = form[k];
            if (pickedTypes.length > 0) body.documents = await uploadDocs();
            await axios.post('/api/client-requests/requests/profile/edit', body);
            setDone(true);
            if (onSubmitted) onSubmitted();
        } catch (err) {
            console.error('Error submitting profile change:', err);
            setError(err.response?.data?.error || 'Failed to submit your changes.');
            setSubmitting(false);
        }
    };

    return (
        <div className="cprof-modal-overlay" onClick={() => !submitting && onClose()}>
            <div className="cprof-modal" onClick={(e) => e.stopPropagation()}>
                {done ? (
                    <div className="cprof-modal__done">
                        <div className="cprof-modal__doneBadge"><FontAwesomeIcon icon={faHourglassHalf} /></div>
                        <h3>Changes submitted</h3>
                        <p>Our team will review your changes. Your details stay as-is until they&apos;re approved.</p>
                        <button className="cprof-btn cprof-btn--primary" onClick={onClose}>Done</button>
                    </div>
                ) : (
                    <>
                        <div className="cprof-modal__head">
                            <h3>Edit Account Details</h3>
                            <button className="cprof-modal__close" onClick={onClose}>&times;</button>
                        </div>
                        <div className="cprof-modal__body">
                            <div className="cprof-modal__note">
                                Changes are reviewed by our team, then applied and synced to your billing on approval.
                            </div>

                            <div className="cprof-modal__section">Account type</div>
                            <div className="cprof-modal__typerow">
                                <button
                                    type="button"
                                    className={`cprof-modal__type ${!isBusiness ? 'is-active' : ''}`}
                                    onClick={() => set({ client_type: 'individual' })}
                                >
                                    <strong>Individual</strong>
                                    <span>Personal account</span>
                                </button>
                                <button
                                    type="button"
                                    className={`cprof-modal__type ${isBusiness ? 'is-active' : ''}`}
                                    onClick={() => set({ client_type: 'business' })}
                                >
                                    <strong>Business</strong>
                                    <span>Company &amp; tax details</span>
                                </button>
                            </div>

                            {isBusiness && (
                                <>
                                    <div className="cprof-modal__section">Company &amp; tax details</div>
                                    <div className="cprof-modal__grid">
                                        <Labeled label="Company name" wide required>
                                            <input value={form.company_name} maxLength={120} onChange={(e) => set({ company_name: e.target.value })} />
                                        </Labeled>
                                        <label className="cprof-modal__check">
                                            <input type="checkbox" checked={form.is_vat_registered} onChange={(e) => set({ is_vat_registered: e.target.checked })} />
                                            VAT registered
                                        </label>
                                        {form.is_vat_registered && (
                                            <Labeled label="TRN number" required>
                                                <input value={form.trn_number} maxLength={40} onChange={(e) => set({ trn_number: e.target.value })} />
                                            </Labeled>
                                        )}
                                    </div>
                                </>
                            )}

                            <div className="cprof-modal__section">Billing address</div>
                            <div className="cprof-modal__grid">
                                <Labeled label="Address" wide required>
                                    <input value={form.address_line_1} maxLength={200} onChange={(e) => set({ address_line_1: e.target.value })} />
                                </Labeled>
                                <Labeled label="City" required>
                                    <input value={form.city} maxLength={80} onChange={(e) => set({ city: e.target.value })} />
                                </Labeled>
                                <Labeled label="State / Emirate" required>
                                    <input value={form.state} maxLength={80} onChange={(e) => set({ state: e.target.value })} />
                                </Labeled>
                                <Labeled label="Country" required>
                                    <input value={form.country} maxLength={80} onChange={(e) => set({ country: e.target.value })} />
                                </Labeled>
                            </div>

                            {isBusiness && (
                                <>
                                    <div className="cprof-modal__section">Documents</div>
                                    <div className="cprof-modal__slots">
                                        <DocSlot
                                            label="Trade License"
                                            hint="Image or PDF"
                                            required
                                            onFile={onFile('trade_license')}
                                            file={files.trade_license}
                                            onPick={(f) => setSlot('trade_license', f)}
                                            onClear={() => setSlot('trade_license', null)}
                                        />
                                        {form.is_vat_registered && (
                                            <DocSlot
                                                label="TRN / VAT Certificate"
                                                hint="Image or PDF"
                                                required
                                                onFile={onFile('vat_certificate')}
                                                file={files.vat_certificate}
                                                onPick={(f) => setSlot('vat_certificate', f)}
                                                onClear={() => setSlot('vat_certificate', null)}
                                            />
                                        )}
                                    </div>
                                </>
                            )}

                            {changedKeys.length > 0 && (
                                <div className="cprof-modal__changes">
                                    <FontAwesomeIcon icon={faCheck} /> Changing: {changedKeys.map((k) => FIELD_LABELS[k]).join(', ')}
                                </div>
                            )}
                            {hasChanges && missing.length > 0 && (
                                <div className="cprof-modal__missing">
                                    Please fill in: {missing.map((k) => FIELD_LABELS[k]).join(', ')}
                                </div>
                            )}
                            {hasChanges && missingDocs.length > 0 && (
                                <div className="cprof-modal__missing">
                                    Please attach: {missingDocs.map((t) => DOC_META[t]).join(', ')}
                                </div>
                            )}
                            {!!error && <div className="cprof-modal__error">{error}</div>}
                        </div>
                        <div className="cprof-modal__actions">
                            <button className="cprof-btn cprof-btn--ghost" onClick={onClose} disabled={submitting}>Cancel</button>
                            <button
                                className="cprof-btn cprof-btn--primary"
                                onClick={submit}
                                disabled={!canSubmit || submitting}
                                title={missing.length > 0 || missingDocs.length > 0
                                    ? 'Complete the required fields and documents first' : undefined}
                            >
                                {submitting ? 'Submitting…' : 'Submit for review'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// Multi-billing (PLAN_multi_billing.md Round 6): the entities this account can
// bill properties to, plus a self-service "request a new one" form. The new
// entity is staged as an approval request — it appears in the list only once
// our team approves it (and its Zoho customer is created).
const EMPTY_ENTITY_REQUEST = {
    entity_type: 'individual',
    first_name: '',
    last_name: '',
    legal_name: '',
    trn_number: '',
    is_vat_registered: false,
    address_line_1: '',
    city: '',
    state: 'Dubai',
    country: 'United Arab Emirates',
};

// Edit mode PRELOADS the entity's current details into the same form shape as
// create (2026-08-10 — blank "keep current" fields were unusable): change
// what you need, submit sends the full picture, and Submit stays disabled
// until something actually differs.
const entityToEditForm = (entity) => ({
    entity_type: entity.entity_type || 'individual',
    first_name: entity.first_name || '',
    last_name: entity.last_name || '',
    legal_name: entity.legal_name || '',
    trn_number: entity.trn_number || '',
    is_vat_registered: !!entity.is_vat_registered,
    address_line_1: entity.address_line_1 || '',
    city: entity.city || '',
    state: entity.state || 'Dubai',
    country: entity.country || 'United Arab Emirates',
});

function BillingEntitiesCard({ entities }) {
    // null | 'create' | the entity row being changed ({billing_profile_id, label})
    const [formTarget, setFormTarget] = useState(null);
    const [form, setForm] = useState({ ...EMPTY_ENTITY_REQUEST });
    const [editInitial, setEditInitial] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const isEdit = !!formTarget && formTarget !== 'create';
    const set = (patch) => setForm((p) => ({ ...p, ...patch }));
    const isBusiness = form.entity_type === 'business';

    // Edit body = the DIFF against the preloaded values — the fields show the
    // full picture, but only what the client actually changed is submitted,
    // so the staff review reads as real changes, not an echo of every field.
    const buildEditBody = () => {
        if (!editInitial) return {};
        const body = {};
        const cmp = (k) => (form[k] || '').trim() !== (editInitial[k] || '').trim();
        if (form.entity_type !== editInitial.entity_type) body.entity_type = form.entity_type;
        if (cmp('legal_name')) body.legal_name = form.legal_name.trim();
        if (cmp('first_name')) body.first_name = form.first_name.trim();
        if (cmp('last_name')) body.last_name = form.last_name.trim();
        if (!!form.is_vat_registered !== !!editInitial.is_vat_registered) body.is_vat_registered = !!form.is_vat_registered;
        if (form.is_vat_registered && cmp('trn_number')) body.trn_number = form.trn_number.trim();
        ['address_line_1', 'city', 'state', 'country'].forEach((k) => {
            if (cmp(k)) body[k] = form[k].trim();
        });
        return body;
    };

    const fieldsValid = isBusiness
        ? !!form.legal_name.trim()
        : !!form.first_name.trim() && !!form.last_name.trim();

    const canSubmit = isEdit
        ? (Object.keys(buildEditBody()).length > 0 && fieldsValid)
        : fieldsValid;

    const openForm = () => {
        setForm({ ...EMPTY_ENTITY_REQUEST });
        setEditInitial(null);
        setError('');
        setDone(false);
        setFormTarget('create');
    };

    const openEditForm = (entity) => {
        const prefilled = entityToEditForm(entity);
        setForm(prefilled);
        setEditInitial(prefilled);
        setError('');
        setDone(false);
        setFormTarget(entity);
    };

    const submit = async () => {
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            if (isEdit) {
                await axios.post(
                    `/api/client-requests/requests/billing-profiles/${formTarget.billing_profile_id}/edit`,
                    buildEditBody()
                );
            } else {
                const body = {
                    entity_type: form.entity_type,
                    is_vat_registered: !!form.is_vat_registered,
                    trn_number: form.is_vat_registered ? form.trn_number.trim() : '',
                    address_line_1: form.address_line_1.trim(),
                    city: form.city.trim(),
                    state: form.state.trim(),
                    country: form.country.trim(),
                };
                if (isBusiness) {
                    body.legal_name = form.legal_name.trim();
                    // Optional contact person on the business entity.
                    if (form.first_name.trim()) body.first_name = form.first_name.trim();
                    if (form.last_name.trim()) body.last_name = form.last_name.trim();
                } else {
                    body.first_name = form.first_name.trim();
                    body.last_name = form.last_name.trim();
                }
                await axios.post('/api/client-requests/requests/billing-profiles', body);
            }
            setDone(true);
            setFormTarget(null);
        } catch (err) {
            console.error('Error requesting billing entity change:', err);
            // 403 / 404 / 409 / 400 all carry a client-facing {error} — show verbatim.
            setError(err.response?.data?.error || 'Failed to submit the request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="cprof__card cprof-bill">
            <div className="cprof__cardTitle">Billing entities</div>
            <div className="cprof-bill__hint">
                Each property is invoiced to one of these entities.
            </div>

            <div className="cprof-bill__list">
                {entities.map((e) => (
                    <div className="cprof-bill__row" key={e.billing_profile_id == null ? 'default' : e.billing_profile_id}>
                        <span className="cprof-bill__name">{e.label}</span>
                        {e.is_default && <span className="cprof-bill__badge">Default</span>}
                        {e.is_default || e.billing_profile_id == null ? (
                            // The default entity IS the account — its details go
                            // through the Edit Details request flow above.
                            <span className="cprof-bill__rowHint">Managed in your account details above</span>
                        ) : (
                            <button
                                type="button"
                                className="cprof-bill__rowBtn"
                                onClick={() => openEditForm(e)}
                            >
                                Request change
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {done && (
                <div className="cprof-bill__done">
                    <FontAwesomeIcon icon={faHourglassHalf} />
                    <div>
                        <strong>Submitted for review</strong>
                        <div>Our team will review your new billing entity — it appears here once approved.</div>
                    </div>
                </div>
            )}

            {!formTarget && !done && (
                <button type="button" className="cprof-bill__addBtn" onClick={openForm}>
                    Request new billing entity
                </button>
            )}

            {formTarget && (
                <div className="cprof-bill__form">
                    {isEdit && (
                        <>
                            <div className="cprof-bill__formTitle">
                                Request changes to “{formTarget.label}”
                            </div>
                            <div className="cprof-bill__formNote">
                                Your current details are prefilled — change what you need
                                and submit. Changes apply after our team approves them.
                            </div>
                        </>
                    )}

                    <div className="cprof-bill__seg">
                        <button
                            type="button"
                            className={`cprof-bill__segBtn ${form.entity_type === 'individual' ? 'is-active' : ''}`}
                            onClick={() => set({ entity_type: 'individual' })}
                        >
                            Individual
                        </button>
                        <button
                            type="button"
                            className={`cprof-bill__segBtn ${isBusiness ? 'is-active' : ''}`}
                            onClick={() => set({ entity_type: 'business' })}
                        >
                            Business
                        </button>
                    </div>

                    <div className="cprof-bill__grid">
                        {isBusiness && (
                            <Labeled label="Business name" wide required>
                                <input value={form.legal_name} maxLength={120} onChange={(e) => set({ legal_name: e.target.value })} placeholder="Registered company name" />
                            </Labeled>
                        )}
                        <Labeled label={isBusiness ? 'Contact first name' : 'First name'} required={!isBusiness}>
                            <input value={form.first_name} maxLength={80} onChange={(e) => set({ first_name: e.target.value })} />
                        </Labeled>
                        <Labeled label={isBusiness ? 'Contact last name' : 'Last name'} required={!isBusiness}>
                            <input value={form.last_name} maxLength={80} onChange={(e) => set({ last_name: e.target.value })} />
                        </Labeled>
                        <label className="cprof-modal__check">
                            <input type="checkbox" checked={form.is_vat_registered} onChange={(e) => set({ is_vat_registered: e.target.checked })} />
                            VAT registered
                        </label>
                        <Labeled label="TRN number">
                            <input
                                value={form.trn_number}
                                maxLength={40}
                                disabled={!form.is_vat_registered}
                                placeholder={form.is_vat_registered ? '15-digit TRN' : 'Tick VAT registered first'}
                                onChange={(e) => set({ trn_number: e.target.value })}
                            />
                        </Labeled>
                        <Labeled label="Address" wide>
                            <input value={form.address_line_1} maxLength={200} onChange={(e) => set({ address_line_1: e.target.value })} />
                        </Labeled>
                        <Labeled label="City">
                            <input value={form.city} maxLength={80} onChange={(e) => set({ city: e.target.value })} />
                        </Labeled>
                        <Labeled label="State / Emirate">
                            <input value={form.state} maxLength={80} onChange={(e) => set({ state: e.target.value })} />
                        </Labeled>
                        <Labeled label="Country">
                            <input value={form.country} maxLength={80} onChange={(e) => set({ country: e.target.value })} />
                        </Labeled>
                    </div>

                    {!!error && <div className="cprof-bill__error">{error}</div>}

                    <div className="cprof-bill__actions">
                        <button type="button" className="cprof-btn cprof-btn--ghost" onClick={() => setFormTarget(null)} disabled={submitting}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="cprof-btn cprof-btn--primary"
                            onClick={submit}
                            disabled={!canSubmit || submitting}
                            title={!canSubmit
                                ? (isEdit
                                    ? 'Fill in at least one field to change'
                                    : (isBusiness ? 'Business name is required' : 'First and last name are required'))
                                : undefined}
                        >
                            {submitting ? 'Submitting…' : 'Submit for review'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Labeled({ label, wide, required, children }) {
    return (
        <label className={`cprof-modal__label ${wide ? 'cprof-modal__label--wide' : ''}`}>
            <span>{label}{required && <span className="cprof-modal__req">*</span>}</span>
            {children}
        </label>
    );
}

// One labelled upload slot: click or drag-drop a single image/PDF; shows a
// preview with a replace/remove control once chosen. `onFile` marks that a
// stored document of this type already satisfies the requirement, so a new
// upload is optional (it replaces the existing one).
function DocSlot({ label, hint, file, onPick, onClear, required, onFile }) {
    const inputRef = React.useRef(null);
    const [dragOver, setDragOver] = useState(false);

    const accept = (f) => f && (f.type.startsWith('image/') || f.type === 'application/pdf');
    const handle = (list) => {
        const f = Array.from(list || [])[0];
        if (!f) return;
        if (!accept(f)) { window.alert('Only image or PDF files are allowed.'); return; }
        onPick(f);
    };

    return (
        <div className="cprof-slot">
            <div className="cprof-slot__label">
                {label}{required && <span className="cprof-modal__req">*</span>}
            </div>
            {onFile && !file && (
                <div className="cprof-slot__onfile">
                    <FontAwesomeIcon icon={faCheck} /> {label} on file
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => { handle(e.target.files); e.target.value = ''; }}
            />
            {file ? (
                <div className="cprof-slot__file">
                    <FontAwesomeIcon
                        icon={file.type === 'application/pdf' ? faFilePdf : faImage}
                        className="cprof-slot__fileIcon"
                    />
                    <span className="cprof-slot__fileName">{file.name}</span>
                    <button type="button" className="cprof-slot__replace" onClick={() => inputRef.current?.click()}>Replace</button>
                    <button type="button" className="cprof-slot__remove" onClick={onClear} title="Remove">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    className={`cprof-slot__drop ${dragOver ? 'is-drag' : ''}`}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); handle(e.dataTransfer.files); }}
                >
                    <FontAwesomeIcon icon={faUpload} className="cprof-slot__dropIcon" />
                    <span className="cprof-slot__dropText">{onFile ? 'Click or drop a file to replace' : 'Click or drop a file'}</span>
                    <span className="cprof-slot__dropHint">{hint}</span>
                </button>
            )}
        </div>
    );
}
